/**
 * DockedSoftphone — the shared softphone "brain", split from the old floating
 * widget. It reads the cross-tab mirror, derives the display mode and exposes
 * the (leader-aware) actions. The two dock containers
 * (`containers/SidebarPhoneDock`, `containers/SlideInPhonePanel`) compose
 * `useSoftphone()` + `SoftphoneBody` with their own chrome.
 *
 * Why a hook + body rather than one mounted component: the sidebar variant
 * mounts inside the <aside>, the slide-in variant at the AppLayout root — two
 * different DOM nodes, selected by PHONE_DOCK_VARIANT.
 */

import { useEffect, useState } from 'react';
import { useTelephonyClient } from '../../hooks/useTelephonyClient';
import { usePhone } from '../../contexts/PhoneProvider';
import type { CallRecord, PhoneStatus } from '../../lib/telephony';
import { MinimizedPill } from './views/MinimizedPill';
import { ExpandedDial } from './views/ExpandedDial';
import { IncomingView } from './views/IncomingView';
import { ActiveView } from './views/ActiveView';
import { DispositionView } from './views/DispositionView';

export type SoftphoneMode = 'minimized' | 'expanded' | 'incoming' | 'active' | 'disposition';

function deriveMode(
  activeCall: CallRecord | null,
  dispositionCall: CallRecord | null,
  dialPadOpen: boolean,
): SoftphoneMode {
  // An active call always wins over a pending disposition — otherwise a stale
  // (unsubmitted) disposition would hide a new incoming call.
  if (activeCall) {
    if (activeCall.state === 'ringing' && activeCall.direction === 'inbound') return 'incoming';
    if (
      activeCall.state === 'active' ||
      activeCall.state === 'dialing' ||
      activeCall.state === 'ringing'
    ) {
      return 'active';
    }
  }
  if (dispositionCall) return 'disposition';
  return dialPadOpen ? 'expanded' : 'minimized';
}

export interface SoftphoneController {
  mode: SoftphoneMode;
  status: PhoneStatus;
  activeCall: CallRecord | null;
  dispositionCall: CallRecord | null;
  isMuted: boolean;
  recoveredToast: string | null;
  answer: () => void;
  reject: () => void;
  hangup: () => void;
  toggleMute: () => void;
  clearDisposition: () => void;
  closeDialPad: () => void;
}

export function useSoftphone(): SoftphoneController {
  const {
    activeCall,
    dispositionCall,
    answer,
    reject,
    hangup,
    toggleMute,
    clearDisposition,
    status,
    isMuted,
  } = useTelephonyClient();
  const { dialPadOpen, closeDialPad } = usePhone();

  // Derived during render (no effect) per react-best-practices.
  const mode = deriveMode(activeCall, dispositionCall, dialPadOpen);

  const [recoveredToast, setRecoveredToast] = useState<string | null>(null);
  useEffect(() => {
    if (activeCall?.recoveredFromCallId) {
      setRecoveredToast('Verbinding hersteld');
      const t = setTimeout(() => setRecoveredToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [activeCall?.recoveredFromCallId]);

  return {
    mode,
    status,
    activeCall,
    dispositionCall,
    isMuted,
    recoveredToast,
    answer,
    reject,
    hangup,
    toggleMute,
    clearDisposition,
    closeDialPad,
  };
}

/** Renders the right view for the current mode. Container-agnostic. */
export function SoftphoneBody({ phone }: { phone: SoftphoneController }) {
  switch (phone.mode) {
    case 'expanded':
      return <ExpandedDial onClose={phone.closeDialPad} />;
    case 'incoming':
      return phone.activeCall ? (
        <IncomingView call={phone.activeCall} onAnswer={phone.answer} onReject={phone.reject} />
      ) : null;
    case 'active':
      return phone.activeCall ? (
        <ActiveView
          call={phone.activeCall}
          onHangup={phone.hangup}
          onToggleMute={phone.toggleMute}
          isMuted={phone.isMuted}
        />
      ) : null;
    case 'disposition':
      return phone.dispositionCall ? (
        <DispositionView call={phone.dispositionCall} onDone={phone.clearDisposition} />
      ) : null;
    case 'minimized':
    default:
      return <MinimizedPill status={phone.status} activeCall={phone.activeCall} />;
  }
}
